<template>
  <v-card :style="`border: ${ game.archived ? '2px solid grey' : '' }`" @click="!importing && $router.push(`/analysis/${game.id}`)" elevation="1" class="mt-6" :class="{ 'cursor-default': importing }">
    <!-- Already Saved Indicator -->
    <v-sheet v-if="importing && alreadySaved" class="pa-2 green lighten-5">
      <div class="d-flex align-center justify-center">
        <v-icon color="green" class="mr-2">mdi-check-circle</v-icon>
        <span class="text-caption font-weight-medium text-green">Tallennettu jo</span>
      </div>
    </v-sheet>

    <!-- Video URL Warning -->
    <v-sheet v-if="importing && hasVideoUrlWarning" class="pa-2 orange lighten-5">
      <div class="d-flex align-center justify-center">
        <v-icon color="orange" class="mr-2">mdi-alert-circle</v-icon>
        <span class="text-caption font-weight-medium text-orange">Jompikumpi videon url virheellinen</span>
      </div>
    </v-sheet>

    <v-row no-gutters>
      <!-- Date and Season Info -->
      <v-col cols="12" sm="2" class="pa-4">
        <div class="text-center">
          <div class="text-body-2 font-weight-medium">
            {{ prettyDate }}
          </div>
          <div class="text-caption text-grey">
            {{ game.season_state }}
          </div>
        </div>
      </v-col>

      <!-- Game Info -->
      <v-col cols="12" sm="8" class="pa-4">
        <!-- Teams and Score -->
        <v-sheet class="d-flex align-center justify-center flex-wrap">
          <div class="d-flex align-center mb-2 mb-sm-0">
            <v-img height="30" width="70" contain :src="game.home_club_logo_url" class="mr-2"></v-img>
            <v-card-title :class="{ 'opacity-half' : game.archived }" class="text-body-1 pa-0">
              <span v-if="$vuetify.breakpoint.mdAndUp">{{game.home_club_name}}</span>
              <span v-else>{{game.home_club_short_name}}</span>
            </v-card-title>
          </div>

          <div class="d-flex align-center mx-2">
            <v-card-title :class="{ 'opacity-half' : game.archived }" class="text-h6 pa-0">{{game.score_home}}</v-card-title>
            <v-card-title :class="{ 'opacity-half' : game.archived }" class="text-h6 pa-0">-</v-card-title>
            <v-card-title :class="{ 'opacity-half' : game.archived }" class="text-h6 pa-0">{{game.score_away}}</v-card-title>
          </div>

          <div class="d-flex align-center">
            <v-card-title :class="{ 'opacity-half' : game.archived }" class="text-body-1 pa-0">
              <span v-if="$vuetify.breakpoint.mdAndUp">{{game.away_club_name}}</span>
              <span v-else>{{game.away_club_short_name}}</span>
            </v-card-title>
            <v-img height="30" width="70" contain :src="game.away_club_logo_url" class="ml-2"></v-img>
          </div>
        </v-sheet>

        <!-- Show external service info when importing -->
        <v-sheet v-if="importing" class="mt-3 pa-3 grey lighten-4 rounded">
          <div class="text-caption">
            <strong>Pesistulokset ID:</strong> {{ game.external_service_id || 'N/A' }}
          </div>
          <div class="text-caption">
            <strong>Video URL:</strong>
            <span class="text-truncate d-inline-block" style="max-width: 200px;" :title="game.video_url || 'N/A'">
              {{ game.video_url || 'N/A' }}
            </span>
          </div>
          <div class="text-caption">
            <strong>Live Video URL:</strong>
            <span class="text-truncate d-inline-block" style="max-width: 200px;" :title="game.video_live_url || 'N/A'">
              {{ game.video_live_url || 'N/A' }}
            </span>
          </div>
        </v-sheet>
      </v-col>

      <!-- Action Buttons -->
      <v-col cols="12" sm="2" class="pa-4 d-flex flex-column align-center justify-center">
        <!-- Show save button when importing -->
        <v-btn
          v-if="importing"
          color="primary"
          small
          :loading="saving"
          :disabled="saving || alreadySaved"
          @click.stop="!alreadySaved && $emit('save', game)"
          class="mb-2"
        >
          <v-icon class="mr-1" v-if="!alreadySaved">mdi-content-save</v-icon>
          <v-icon class="mr-1" v-else>mdi-check</v-icon>
          <span v-if="$vuetify.breakpoint.smAndUp">
            {{ alreadySaved ? 'Tallennettu jo' : (saving ? 'Tallennetaan...' : 'Tallenna') }}
          </span>
          <span v-else>
            {{ alreadySaved ? 'Tallennettu' : (saving ? '...' : 'Tallenna') }}
          </span>
        </v-btn>

        <!-- Show regular buttons when not importing -->
        <template v-else>
          <div class="d-flex flex-column align-center">
            <v-btn
              fab
              x-small
              text
              @click.stop="$emit('edit')"
              class="mb-1"
            >
              <v-icon>mdi-pencil-outline</v-icon>
            </v-btn>
            <v-btn
              fab
              x-small
              text
              @click.stop="handleArchivingGame"
              class="mb-1"
            >
              <v-icon v-if="!game.archived">mdi-archive-outline</v-icon>
              <v-icon v-else>mdi-archive-off-outline</v-icon>
            </v-btn>
            <v-btn
              :disabled="!game.will_be_analyzed"
              v-tooltip="'Onko käsitelty'"
              :color="game.analyzed ? 'green' : ''"
              small
              @click.stop="toggleAnalyzed"
            >
              <v-icon>mdi-check</v-icon>
            </v-btn>
          </div>
        </template>
      </v-col>
    </v-row>
  </v-card>
</template>

<script>
import { mapActions } from 'vuex';

export default {
  props: {
    game: Object,
    importing: {
      type: Boolean,
      default: false
    },
    saving: {
      type: Boolean,
      default: false
    },
    alreadySaved: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    prettyDate() {
      const date = new Date(this.game.starttime_unix)
      return date.getDate() + '.' + (date.getMonth() + 1) + '.' + date.getFullYear()
    },
    hasVideoUrlWarning() {
      if (!this.importing) return false

      // UUID regex pattern
      const uuidPattern = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i

      const videoUrl = this.game.video_url || ''
      const liveVideoUrl = this.game.video_live_url || ''

      // Check if URLs exist
      const hasVideoUrl = videoUrl.length > 0
      const hasLiveVideoUrl = liveVideoUrl.length > 0

      // Check if URLs contain UUID
      const videoUrlHasUuid = hasVideoUrl && uuidPattern.test(videoUrl)
      const liveVideoUrlHasUuid = hasLiveVideoUrl && uuidPattern.test(liveVideoUrl)

      // Show warning if either URL is missing or either URL exists but doesn't have UUID
      return !hasVideoUrl || !hasLiveVideoUrl || !videoUrlHasUuid || !liveVideoUrlHasUuid
    }
  },
  methods: {
    ...mapActions('noti', [
      'info',
      'error'
    ]),
    ...mapActions('admin', [
      'editGame'
    ]),
    toggleAnalyzed() {
      const payload = {
        ...this.game,
        analyzed: !this.game.analyzed
      }
      const locked_payload = JSON.parse(JSON.stringify(payload))
      this.editGame(locked_payload)
        .then(() => {
          this.info('Game analyzed toggled ', payload.analyzed ? 'on' : 'off')
        })
        .catch(() => {
          this.error('Failed to toggle game archiving')
        })
    },
    handleArchivingGame() {
      const payload = {
        ...this.game,
        archived: !this.game.archived
      }
      const locked_payload = JSON.parse(JSON.stringify(payload))
      console.log(locked_payload)
      this.editGame(locked_payload)
        .then(() => {
          this.info('Game archiving toggled ', payload.archived ? 'on' : 'off')
        })
        .catch(() => {
          this.error('Failed to toggle game archiving')
        })
    }
  }
}
</script>

<style scoped>
.opacity-half {
  opacity: 0.5;
}
.cursor-default {
  cursor: default !important;
}
</style>